import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/offerModal.css";
import { isSpecialOfferActive } from "../utils/offerDateUtil";

const SESSION_STORAGE_KEY = "vk_landing_offer_seen_v1";

// Handcrafted Vector Brass Diya with Animated Glowing Flame (Zero Emojis)
const VectorDiya = () => (
  <svg className="vk-svg-diya" viewBox="0 0 50 40" fill="none">
    <defs>
      <radialGradient id="vkFlameGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFF275" stopOpacity="0.9" />
        <stop offset="45%" stopColor="#FF9900" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#FF3300" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="vkFlameGrad" x1="0%" y1="100%" x2="0%" y2="0%">
        <stop offset="0%" stopColor="#E65100" />
        <stop offset="40%" stopColor="#FB8C00" />
        <stop offset="75%" stopColor="#FDD835" />
        <stop offset="100%" stopColor="#FFFFFF" />
      </linearGradient>
      <linearGradient id="vkBrassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFE082" />
        <stop offset="35%" stopColor="#FFC107" />
        <stop offset="70%" stopColor="#FFA000" />
        <stop offset="100%" stopColor="#FF6F00" />
      </linearGradient>
    </defs>
    {/* Flame glow */}
    <circle cx="25" cy="14" r="13" fill="url(#vkFlameGlow)" />
    {/* Animated flame */}
    <g className="vk-diya-flame-anim">
      <path
        d="M25 3 C22 9, 20 14, 20 18 C20 21, 22 23, 25 23 C28 23, 30 21, 30 18 C30 14, 28 9, 25 3 Z"
        fill="url(#vkFlameGrad)"
      />
      <path
        d="M25 7 C23 11, 22 14, 22 17 C22 19, 23 20.5, 25 20.5 C27 20.5, 28 19, 28 17 C28 14, 27 11, 25 7 Z"
        fill="#FFFDE7"
      />
    </g>
    {/* Diya Brass Bowl Base */}
    <path
      d="M6 21 Q25 19 44 21 Q41 33 25 35 Q9 33 6 21 Z"
      fill="url(#vkBrassGrad)"
      stroke="#B8860B"
      strokeWidth="0.8"
    />
    <ellipse cx="25" cy="21" rx="19" ry="3.5" fill="#FFEAA7" opacity="0.8" />
    <ellipse cx="25" cy="35" rx="8" ry="2" fill="#8D6E63" />
  </svg>
);

const LandingOfferModal = () => {
  const [show, setShow] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // First time popup logic on landing page (active Oct 6 - Nov 6)
  useEffect(() => {
    if (location.pathname === "/" && isSpecialOfferActive()) {
      const hasSeen = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (!hasSeen) {
        const timer = setTimeout(() => {
          setShow(true);
        }, 700);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname]);

  // Listener to open modal on request (e.g. news ticker click or "View Deals")
  useEffect(() => {
    const handleOpen = () => setShow(true);
    window.addEventListener("vk_open_offer_modal", handleOpen);
    return () => window.removeEventListener("vk_open_offer_modal", handleOpen);
  }, []);

  const handleClose = useCallback(() => {
    setShow(false);
    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
  }, []);

  const handleShopNow = () => {
    handleClose();
    navigate("/product");
  };

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && show) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [show, handleClose]);

  if (!show) return null;

  return (
    <div
      className="vk-offer-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="vk-modal-title"
    >
      <div
        className="vk-offer-modal-card"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/diwali_fantasy_bg.jpg)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          className="vk-modal-close-btn"
          onClick={handleClose}
          aria-label="Close offer modal"
        >
          ×
        </button>

        {/* ========================================================
            HEADER: Option 1 Traditional & Elegant Look
            With Official Logo, Hanging Lanterns, Festive Crackers
           ======================================================== */}
        <div className="vk-diwali-header">
          {/* Left Hanging Lantern */}
          <svg className="vk-lantern-left" viewBox="0 0 40 80" fill="none">
            <line x1="20" y1="0" x2="20" y2="28" stroke="#D4AF37" strokeWidth="1.5" />
            <ellipse cx="20" cy="28" rx="6" ry="2.5" fill="#D4AF37" />
            <path d="M12 30 L28 30 L32 50 L8 50 Z" fill="url(#lanternGoldGrad)" stroke="#B8860B" strokeWidth="1" />
            <ellipse cx="20" cy="50" rx="12" ry="3" fill="#D4AF37" />
            <ellipse cx="20" cy="42" rx="4" ry="5" fill="#FFF275" opacity="0.9" />
            <polygon points="17,53 23,53 20,62" fill="#D4AF37" />
            <defs>
              <linearGradient id="lanternGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="50%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#8A4B27" />
              </linearGradient>
            </defs>
          </svg>

          {/* Right Hanging Lantern */}
          <svg className="vk-lantern-right" viewBox="0 0 40 80" fill="none">
            <line x1="20" y1="0" x2="20" y2="22" stroke="#D4AF37" strokeWidth="1.5" />
            <ellipse cx="20" cy="22" rx="6" ry="2.5" fill="#D4AF37" />
            <path d="M12 24 L28 24 L32 44 L8 44 Z" fill="url(#lanternGoldGrad2)" stroke="#B8860B" strokeWidth="1" />
            <ellipse cx="20" cy="44" rx="12" ry="3" fill="#D4AF37" />
            <ellipse cx="20" cy="36" rx="4" ry="5" fill="#FFF275" opacity="0.9" />
            <polygon points="17,47 23,47 20,56" fill="#D4AF37" />
            <defs>
              <linearGradient id="lanternGoldGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="50%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#8A4B27" />
              </linearGradient>
            </defs>
          </svg>

          {/* Left Festive Cracker Sparkler Burst */}
          <svg className="vk-cracker-burst vk-cracker-top-left" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="3" fill="#FFF275" filter="drop-shadow(0 0 4px #FFD700)" />
            <line x1="30" y1="30" x2="30" y2="6" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="50" y2="12" stroke="#FFD700" strokeWidth="1.2" />
            <line x1="30" y1="30" x2="54" y2="30" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="48" y2="48" stroke="#FFD700" strokeWidth="1.2" />
            <line x1="30" y1="30" x2="30" y2="54" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="12" y2="48" stroke="#FFD700" strokeWidth="1.2" />
            <line x1="30" y1="30" x2="6" y2="30" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="12" y2="12" stroke="#FFD700" strokeWidth="1.2" />
            <circle cx="30" cy="6" r="1.5" fill="#FFE066" />
            <circle cx="50" cy="12" r="1.8" fill="#FFF" />
            <circle cx="54" cy="30" r="1.5" fill="#FFE066" />
            <circle cx="48" cy="48" r="1.8" fill="#FFF" />
            <circle cx="30" cy="54" r="1.5" fill="#FFE066" />
            <circle cx="12" cy="48" r="1.8" fill="#FFF" />
            <circle cx="6" cy="30" r="1.5" fill="#FFE066" />
            <circle cx="12" cy="12" r="1.8" fill="#FFF" />
          </svg>

          {/* Right Festive Cracker Sparkler Burst */}
          <svg className="vk-cracker-burst vk-cracker-top-right" viewBox="0 0 60 60" fill="none">
            <circle cx="30" cy="30" r="3" fill="#FFF275" filter="drop-shadow(0 0 4px #FFD700)" />
            <line x1="30" y1="30" x2="30" y2="6" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="50" y2="12" stroke="#FFD700" strokeWidth="1.2" />
            <line x1="30" y1="30" x2="54" y2="30" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="48" y2="48" stroke="#FFD700" strokeWidth="1.2" />
            <line x1="30" y1="30" x2="30" y2="54" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="12" y2="48" stroke="#FFD700" strokeWidth="1.2" />
            <line x1="30" y1="30" x2="6" y2="30" stroke="#FFD700" strokeWidth="1.2" strokeDasharray="3 2" />
            <line x1="30" y1="30" x2="12" y2="12" stroke="#FFD700" strokeWidth="1.2" />
            <circle cx="30" cy="6" r="1.5" fill="#FFE066" />
            <circle cx="50" cy="12" r="1.8" fill="#FFF" />
            <circle cx="54" cy="30" r="1.5" fill="#FFE066" />
            <circle cx="48" cy="48" r="1.8" fill="#FFF" />
            <circle cx="30" cy="54" r="1.5" fill="#FFE066" />
            <circle cx="12" cy="48" r="1.8" fill="#FFF" />
            <circle cx="6" cy="30" r="1.5" fill="#FFE066" />
            <circle cx="12" cy="12" r="1.8" fill="#FFF" />
          </svg>

          {/* Official Brand Logo */}
          <div className="vk-brand-logo-area">
            <div className="vk-logo-pill">
              <img
                src={`${process.env.PUBLIC_URL}/Logo.png`}
                alt="Vannakkarangal"
                className="vk-diwali-logo"
              />
            </div>
          </div>

          {/* Diwali Single-Line Title Banner with Integrated Ribbon */}
          <div className="vk-diwali-title-badge">
            <h1 id="vk-modal-title" className="vk-diwali-single-title">
              DIWALI STENCIL SALE
            </h1>
            <div className="vk-ribbon-tag">BUY MORE • GET MORE!</div>
          </div>

          {/* Date Validity Pill: OCT 6 – NOV 6 ONLY */}
          <div className="vk-validity-pill-wrap">
            <div className="vk-validity-pill">
              <i className="fa-solid fa-calendar-days me-1" />
              <span>OCT 6 – NOV 6 ONLY</span>
            </div>
          </div>

          <p className="vk-header-desc">
            Shop your favourite stencils and enjoy exclusive festive freebies!
          </p>
        </div>

        {/* ========================================================
            BODY: 3 Product Offer Cards with Preview Images (No Emojis)
           ======================================================== */}
        <div className="vk-modal-body">
          {/* Card 1: MANDALA STENCILS */}
          <div className="vk-option1-card">
            <div className="vk-card-pill-header vk-pill-mandala">
              <span>MANDALA STENCILS</span>
            </div>

            <div className="vk-card-grid-content">
              {/* Product Preview Image */}
              <div className="vk-product-thumb-wrap">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/offer_mandala.png`}
                  alt="Mandala Stencils with Bridal Henna Cone"
                />
              </div>

              {/* Deal Rows */}
              <div className="vk-card-deals-col">
                <div className="vk-deal-badge-row">
                  <span className="condition">Buy 2</span>
                  <span className="arrow">➔</span>
                  <span className="reward">1 Bridal Cone FREE</span>
                </div>
                <div className="vk-deal-badge-row">
                  <span className="condition">Buy 3</span>
                  <span className="arrow">➔</span>
                  <span className="reward">2 Bridal Cones FREE</span>
                </div>
                <div className="vk-deal-badge-row gold-tier">
                  <span className="condition">Buy 5</span>
                  <span className="arrow">➔</span>
                  <span className="reward">5 Bridal Cones FREE</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: BRIDAL STENCILS */}
          <div className="vk-option1-card">
            <div className="vk-card-pill-header vk-pill-bridal">
              <span>BRIDAL STENCILS</span>
            </div>

            <div className="vk-card-grid-content">
              {/* Product Preview Image */}
              <div className="vk-product-thumb-wrap">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/offer_bridal.png`}
                  alt="Bridal Stencils with Henna Dip Jar"
                />
              </div>

              {/* Deal Row */}
              <div className="vk-card-deals-col">
                <div className="vk-deal-badge-row gold-tier">
                  <span className="condition">Every Bridal Stencil</span>
                  <span className="arrow">➔</span>
                  <span className="reward">1 Henna Dip FREE</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: SEMI-BRIDAL STENCILS */}
          <div className="vk-option1-card">
            <div className="vk-card-pill-header vk-pill-semibridal">
              <span>SEMI-BRIDAL STENCILS</span>
            </div>

            <div className="vk-card-grid-content">
              {/* Product Preview Image */}
              <div className="vk-product-thumb-wrap">
                <img
                  src={`${process.env.PUBLIC_URL}/assets/offer_semibridal.png`}
                  alt="Semi-Bridal Stencils with Henna Dip Jar"
                />
              </div>

              {/* Deal Row */}
              <div className="vk-card-deals-col">
                <div className="vk-deal-badge-row gold-tier">
                  <span className="condition">Buy 2</span>
                  <span className="arrow">➔</span>
                  <span className="reward">1 Henna Dip FREE</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================
            FOOTER: 4 Trust Badges (FontAwesome Vector Icons) &
            Glowing Gold CTA Button flanked by Handcrafted Vector Diyas
           ======================================================== */}
        <div className="vk-option1-footer">
          {/* Trust Badges Grid - Crisp Vector Icons */}
          <div className="vk-trust-grid">
            <div className="vk-trust-item">
              <span className="vk-trust-icon-wrap">
                <i className="fa-solid fa-gem" />
              </span>
              <span className="vk-trust-label">Beautiful Designs</span>
            </div>
            <div className="vk-trust-item">
              <span className="vk-trust-icon-wrap">
                <i className="fa-solid fa-gift" />
              </span>
              <span className="vk-trust-label">Festive Freebies</span>
            </div>
            <div className="vk-trust-item">
              <span className="vk-trust-icon-wrap">
                <i className="fa-solid fa-box-open" />
              </span>
              <span className="vk-trust-label">Limited Stock</span>
            </div>
            <div className="vk-trust-item">
              <span className="vk-trust-icon-wrap">
                <i className="fa-solid fa-truck-fast" />
              </span>
              <span className="vk-trust-label">Fast Dispatch</span>
            </div>
          </div>

          {/* CTA Button with Vector Brass Diyas (Zero Local Emojis) */}
          <div className="vk-cta-btn-wrap">
            <VectorDiya />
            <button className="vk-btn-gold-shop" onClick={handleShopNow}>
              <i className="fa-solid fa-bag-shopping me-2" />
              <span>Shop Stencils Now &gt;</span>
            </button>
            <VectorDiya />
          </div>

          {/* Promotional Terms Note */}
          <p className="vk-offer-footer-note">
            Free gifts automatically included with qualifying orders placed between Oct 6 to Nov 6 only
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingOfferModal;
